import { yupResolver } from "@hookform/resolvers/yup";
import { KeyboardBackspaceOutlined } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../components";
import useAuth from "../../hooks/useAuth";
import { useCategories } from "../../hooks/useCategories";
import { useCities } from "../../hooks/useCities";
import {
  createNGO_V2,
  getNGOById_V2,
  updateAdminNGO_V2,
  uploadNgoImage_V2,
} from "../../Services_V2/NGO.service";
import { showError } from "../../utils/error";
import styles from "./AddEditNGOPage.module.css";
import { INGOForm, NgoInputs, ngoSchema } from "./const";
import NGOFormFields from "./NGOFormFields";

export interface IProp {
  settingNgoId?: string;
}

const AddEditNGOPage: React.FC<IProp> = ({ settingNgoId }) => {
  const navigate = useNavigate();
  const { ngoId } = useParams<{ ngoId?: string }>();

  const resolvedNgoId = settingNgoId || ngoId;
  const isEdit = !!resolvedNgoId;
  const { options: categoryOptions, refetch: refetchCategory } =
    useCategories();
  const { options: cityOptions, refetch: refetchCities } = useCities();

  const {
    state: { user },
  } = useAuth();

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [UpiQrPreview, setUpiQrPreview] = useState<string | null>(null);
  const [cert80GPreview, setCert80GPreview] = useState<string | null>(null);
  const [trustDeedPreview, setTrustDeedPreview] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
  } = useForm<INGOForm>({
    resolver: yupResolver<INGOForm>(ngoSchema(isEdit)),
    defaultValues: {
      email: "",
      websiteUrl: "",
      contact_person: { name: "", email: "", number: "" },
      cities: [],
    },
  });

  // Fetch NGO data for edit mode
  useEffect(() => {
    if (!isEdit || !resolvedNgoId) return;

    const loadNGO = async () => {
      try {
        setFetching(true);

        const response = await getNGOById_V2(resolvedNgoId, user?.role);

        const ngo = response?.data || response;

        if (!ngo) {
          toast.error("NGO data not found.");
          navigate(-1);
          return;
        }

        reset({
          [NgoInputs.NAME]: ngo.name || "",
          [NgoInputs.TAGLINE]: ngo.tagline || "",
          [NgoInputs.SUMMARY]: ngo.summary || "",
          [NgoInputs.ABOUT]: ngo.about || "",
          [NgoInputs.CATEGORY]: ngo.category?._id || ngo.category || "",
          [NgoInputs.DARPAN_ID]: ngo.darpanId || "",
          [NgoInputs.WEBSITE]: ngo.websiteUrl || "",
          email: ngo.email || "",
          cities:
            ngo.cities?.map((c: any) => (typeof c === "object" ? c._id : c)) ||
            [],
          contact_person: {
            name: ngo.contact_person?.name || "",
            email: ngo.contact_person?.email || "",
            number: ngo.contact_person?.number || "",
          },
          bankDetails: {
            bankName: ngo.bankDetails?.bankName || "",
            branch: ngo.bankDetails?.branch || "",
            ifscCode: ngo.bankDetails?.ifscCode || "",
            accountType: ngo.bankDetails?.accountType || "",
            beneficiaryName: ngo.bankDetails?.beneficiaryName || "",
            accountNumber: ngo.bankDetails?.accountNumber || "",
            upiId: ngo.bankDetails?.upiId || "",
          },
        });

        if (ngo.logo?.url) {
          setLogoPreview(ngo.logo.url);
        }
        if (ngo?.donationQr?.url) {
          setUpiQrPreview(ngo.donationQr.url);
        }
        if (ngo.eightyGCertificate?.url) {
          setCert80GPreview(ngo.eightyGCertificate.url);
        }
        if (ngo.trustDeedCertificate?.url) {
          setTrustDeedPreview(ngo.trustDeedCertificate.url);
        }
      } catch (error) {
        showError(error);
        toast.error("Failed to load NGO details.");
        navigate(-1);
      } finally {
        setFetching(false);
      }
    };

    loadNGO();
  }, [resolvedNgoId, isEdit, reset, navigate, user?.role]);

  useEffect(() => {
    refetchCities?.();
    refetchCategory?.();
  }, []);

  const watchedLogo = watch(NgoInputs.NGO_IMAGE);
  const watched80G = watch(NgoInputs.EIGHTY_G_CERTIFICATE);
  const watchedTrustDeed = watch(NgoInputs.TRUST_DEED_CERTIFICATE);

  useEffect(() => {
    return () => {
      [logoPreview, cert80GPreview, trustDeedPreview].forEach((url) => {
        if (url && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [logoPreview, cert80GPreview, trustDeedPreview]);

  const onSubmit = async (values: INGOForm) => {
    setLoading(true);

    try {
      if (!isEdit) {
        // ==================== CREATE MODE ====================
        const formData = new FormData();

        formData.append("name", values.name);
        if (values.email) formData.append("email", values.email);

        if (values.tagline) formData.append("tagline", values.tagline);

        if (values.summary) formData.append("summary", values.summary);

        if (values.about) formData.append("about", values.about);

        formData.append("category", values.category);
        formData.append("darpanId", values.darpanId || "");
        formData.append("websiteUrl", values.websiteUrl || "");

        formData.append(
          "contact_person[name]",
          values.contact_person.name ?? "",
        );
        formData.append(
          "contact_person[email]",
          values.contact_person.email ?? "",
        );
        formData.append(
          "contact_person[number]",
          values.contact_person.number ?? "",
        );

        values.cities.forEach((city) => formData.append("cities[]", city));

        if (values.logo) formData.append("logo", values.logo);
        if (values.donationQr) formData.append("donationQr", values.donationQr);
        if (values.eightyGCertificate) {
          formData.append("eightyGCertificate", values.eightyGCertificate);
        }
        if (values.trustDeedCertificate) {
          formData.append("trustDeedCertificate", values.trustDeedCertificate);
        }
        if (values.bankDetails.bankName) {
          formData.append("bankDetails[bankName]", values.bankDetails.bankName);
        }
        if (values.bankDetails.branch) {
          formData.append("bankDetails[branch]", values.bankDetails.branch);
        }
        if (values.bankDetails.ifscCode) {
          formData.append("bankDetails[ifscCode]", values.bankDetails.ifscCode);
        }
        if (values.bankDetails.accountType)
          formData.append(
            "bankDetails[accountType]",
            values.bankDetails.accountType,
          );
        if (values.bankDetails.beneficiaryName)
          formData.append(
            "bankDetails[beneficiaryName]",
            values.bankDetails.beneficiaryName,
          );
        if (values.bankDetails.accountNumber)
          formData.append(
            "bankDetails[accountNumber]",
            values.bankDetails.accountNumber,
          );
        if (values.bankDetails.upiId) {
          formData.append("bankDetails[upiId]", values.bankDetails.upiId);
        }

        const res = await createNGO_V2(formData);

        if (res?.success) {
          toast.success(res.message || "NGO created successfully");
          {
            !settingNgoId && navigate(-1);
          }
        }
      } else {
        // ===== EDIT MODE =====
        const payload = {
          name: values.name,
          ...(values.email && { email: values.email }),
          tagline: values.tagline,
          summary: values.summary,
          about: values.about,
          category: values.category,
          darpanId: values.darpanId || "",
          websiteUrl: values.websiteUrl || "",
          cities: values.cities,
          ...(values.bankDetails &&
            Object.values(values.bankDetails).some(
              (v) => v !== undefined && v !== null && v !== "",
            ) && {
              bankDetails: {
                bankName: values.bankDetails.bankName ?? "",
                branch: values.bankDetails.branch ?? "",
                ifscCode: values.bankDetails.ifscCode ?? "",
                accountType: values.bankDetails.accountType,
                beneficiaryName: values.bankDetails.beneficiaryName ?? "",
                accountNumber: values.bankDetails.accountNumber ?? "",
                upiId: values.bankDetails.upiId ?? "",
              },
            }),
        };

        const res = await updateAdminNGO_V2(resolvedNgoId!, payload);

        const fileUploads: Promise<any>[] = [];

        if (watchedLogo instanceof File) {
          const logoFormData = new FormData();
          logoFormData.append("logo", watchedLogo);
          fileUploads.push(uploadNgoImage_V2(resolvedNgoId!, logoFormData));
        }

        if (watched80G instanceof File) {
          const certFormData = new FormData();
          certFormData.append("eightyGCertificate", watched80G);
          fileUploads.push(uploadNgoImage_V2(resolvedNgoId!, certFormData));
        }

        if (watchedTrustDeed instanceof File) {
          const deedFormData = new FormData();
          deedFormData.append("trustDeedCertificate", watchedTrustDeed);
          fileUploads.push(uploadNgoImage_V2(resolvedNgoId!, deedFormData));
        }

        // If any files to upload
        if (fileUploads.length > 0) {
          try {
            await Promise.all(fileUploads);
            toast.success("Files uploaded successfully");
          } catch (fileError) {
            toast.error("Some files failed to upload");
            console.error(fileError);
          }
        }

        {
          !settingNgoId && navigate(-1);
        }
        toast.success(res?.message || "NGO updated successfully");
      }
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  if (fetching) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <button onClick={() => navigate(-1)} className={styles.backButton}>
            <KeyboardBackspaceOutlined />
          </button>
          <h2>Loading NGO...</h2>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <button onClick={() => navigate(-1)} className={styles.backButton}>
            <KeyboardBackspaceOutlined />
          </button>
          <h2>{isEdit ? "Edit NGO" : "Add New NGO"}</h2>
        </div>

        <div className={styles.formCard}>
          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <NGOFormFields
              register={register}
              control={control}
              errors={errors}
              categoryOptions={categoryOptions}
              cityOptions={cityOptions}
              isEdit={isEdit}
              logoPreview={logoPreview}
              cert80GPreview={cert80GPreview}
              trustDeedPreview={trustDeedPreview}
              setLogoPreview={setLogoPreview}
              setCert80GPreview={setCert80GPreview}
              setTrustDeedPreview={setTrustDeedPreview}
              resolvedNgoId={resolvedNgoId}
              UpiQrPreview={UpiQrPreview}
              setUpiQrPreview={setUpiQrPreview}
            />

            <div className={styles.actions}>
              <Button variant="outlined" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button type="submit" loading={loading}>
                {isEdit ? "Update NGO" : "Create NGO"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddEditNGOPage;
